'use client'

import { type ReleaseInfo } from '@/lib/github'
import { GlobalNavSubMenuColLatestReleaseStoryblok } from '@/lib/types/component-types-sb'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import Link from 'next/link'
import { ComponentProps } from 'react'
import { StoryblokImage } from '../storyblok-image'

export function NavSubMenuLatestRelease({
  col,
  className,
  ...props
}: {
  col: GlobalNavSubMenuColLatestReleaseStoryblok
} & Omit<ComponentProps<typeof Link>, 'href' | 'target'>) {
  const { data: latestRelease, isLoading } = useQuery<ReleaseInfo>({
    queryKey: ['latestRelease'],
    queryFn: async () => {
      const response = await fetch('/api/github/latest-release')
      if (!response.ok) {
        throw new Error('Failed to fetch latest release')
      }
      return response.json()
    },
  })

  if (isLoading) {
    return <div className="text-muted-foreground p-2.5 text-sm">Loading latest release...</div>
  }

  if (!latestRelease) {
    return (
      <div className="text-muted-foreground p-2.5 text-sm">No release information available</div>
    )
  }

  return (
    <Link
      href={'/changelog?focus=' + latestRelease.tag_name}
      className={cn('px-3', className)}
      {...props}
    >
      <div className="bg-accent relative mb-3 h-44 w-full overflow-hidden rounded-md lg:h-36">
        {col.image.filename && (
          <StoryblokImage
            image={col.image}
            className="absolute bottom-0 left-0 z-10 h-full w-full object-cover object-center"
          />
        )}
        <div className="absolute inset-0 z-20 h-full w-full bg-gradient-to-b from-transparent to-black/60"></div>
        <span className="text-accent-foreground absolute bottom-2.5 left-2.5 z-30 font-mono text-3xl font-medium">
          {latestRelease.name}
        </span>
      </div>
      <p className="text-muted-foreground text-xs">
        Check out our latest release {latestRelease.tag_name} published on{' '}
        {format(new Date(latestRelease.published_at), 'MMM d, yyyy')}
      </p>
    </Link>
  )
}
